/**
 * The tiles a module's playlist draws.
 *
 * A module is a row of capsule tiles — the card's carousel, the module's own
 * screen and the studio's slots are the same row in three places — and each tile
 * has to be *resolved* before anything can draw it: what to call it, how much work
 * is in it, which picture goes on it, and whether there is anything behind it at
 * all. That resolution is here, pure, because three callers doing it separately is
 * three chances for the same capsule to be called something different on the card
 * than in the studio.
 *
 * ## Where each fact comes from, and why in that order
 *
 * The two records a tile can be built from arrive at different speeds and neither
 * is complete on its own:
 *
 *  - the **post** the capsule was published as is what everything that *draws*
 *    work reads: its text carries the title the creator typed, its own `slideCount`
 *    is the exercise count the publisher copied onto it, and its video embed is
 *    where the appview's thumbnail of the cover film lives. It arrives in one
 *    batched request for the whole playlist.
 *  - the **capsule record** is where the *facts* live — the level, the subject,
 *    whether it is a draft — and it is only in hand in the studio, which is the one
 *    place that needs them. It also carries its own cover film for a capsule
 *    written for a room, which has no post to carry it (see `#/lib/capsuleCover`).
 *
 * So the post wins where the two overlap (it is the thing the reader will meet),
 * the record fills in what the post cannot say, and a place with neither is
 * *missing* rather than drawn blank — which is the state a creator has to be able
 * to see and fix, whether the capsule was deleted or the request has not landed.
 */
import {type ModuleCapsuleFacts, type ModuleItem} from '#/lib/modulePlaylist'

/** A post, as loosely as the feed hands it over — extra fields and all. */
type Postish = {
  uri?: unknown
  record?: unknown
  embed?: unknown
}

/**
 * What the studio knows about a capsule beyond what its post says.
 *
 * A structural extension of `ModuleCapsuleFacts` — an `ActorCapsuleRecord`
 * satisfies it as it stands — with the one field a *picture* needs: the cover
 * frame a capsule carrying its own film can be drawn from when no post exists.
 */
export type ModuleTileFacts = ModuleCapsuleFacts & {
  /** The record's own cover film frame, when it has one. */
  coverUri?: string
}

/** One place in the playlist, ready to draw. */
export type ModuleTile = {
  /** Stable across re-renders and re-orderings: the capsule's own address. */
  key: string
  uri: string
  /** Where it sits in the playlist, from 1 — what the tile's number shows. */
  position: number
  title: string
  /** The capsule's exercises, as the publisher counted them. */
  coins: number
  coverUri?: string
  /**
   * `ready` is a tile with a post behind it; `draft` one that was written and not
   * published; `missing` one whose record cannot be read; `loading` one the caller
   * is still asking about, which is deliberately *not* the same as missing — a
   * card does not get to claim a capsule has been deleted while it is in flight.
   */
  state: 'ready' | 'draft' | 'missing' | 'loading'
  postUri?: string
}

/** The first non-empty line of a post's own text — the title the creator typed. */
function firstLineOf(text: unknown): string {
  if (typeof text !== 'string') return ''
  const line = text.split('\n').find(part => part.trim().length > 0)
  return line?.trim() ?? ''
}

/** A still, however the two records spell it. */
function thumbnailOf(post: Postish | undefined): string | undefined {
  const embed = post?.embed as {thumbnail?: unknown} | undefined
  return typeof embed?.thumbnail === 'string' && embed.thumbnail
    ? embed.thumbnail
    : undefined
}

/** A whole count, or `undefined` — never a string dressed as a number. */
function count(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : undefined
}

/**
 * The tiles for a playlist.
 *
 * `capsules` is what the caller knows about the items it is drawing: the studio
 * passes the author's own records (which is how a draft is recognised), and a card
 * in a feed passes nothing, because a published module cannot hold a capsule
 * without a post and the post is what it draws from.
 *
 * `isPending` is the honest half of `missing`. A tile whose post has not arrived
 * and whose request is still out is *loading*; once the request has settled it is
 * gone, and the two must not look the same, because one is a moment and the other
 * is a capsule the creator has to replace.
 */
export function moduleTiles({
  items,
  capsules,
  posts,
  isPending,
}: {
  items: readonly ModuleItem[]
  capsules?: readonly ModuleTileFacts[]
  posts?: readonly Postish[]
  isPending?: boolean
}): ModuleTile[] {
  const factsByUri = new Map(
    (capsules ?? []).map(capsule => [capsule.uri, capsule]),
  )
  const postsByUri = new Map<string, Postish>()
  for (const post of posts ?? []) {
    if (typeof post.uri === 'string') postsByUri.set(post.uri, post)
  }

  return items.map((item, index) => {
    const facts = factsByUri.get(item.uri)
    /*
     * The post, asked for by the address the item carries.
     *
     * A capsule published before modules carried the post's address (or one whose
     * item was written by hand) has none — which is why the record's own facts are
     * read as a fallback rather than the post being the only source.
     */
    const postUri = item.postUri ?? facts?.postUri
    const post = postUri ? postsByUri.get(postUri) : undefined
    const postRecord = (post?.record ?? {}) as Record<string, unknown>

    const title =
      facts?.title?.trim() || firstLineOf(postRecord.text) || 'Untitled capsule'
    const coins = count(facts?.slideCount) ?? count(postRecord.slideCount) ?? 0
    const coverUri = thumbnailOf(post) ?? facts?.coverUri

    const published = facts ? facts.status !== 'draft' && !!postUri : !!post

    const state: ModuleTile['state'] = published
      ? 'ready'
      : facts?.status === 'draft'
        ? 'draft'
        : isPending
          ? 'loading'
          : 'missing'

    return {
      key: item.uri,
      uri: item.uri,
      position: index + 1,
      title,
      coins,
      ...(coverUri ? {coverUri} : null),
      state,
      ...(postUri ? {postUri} : null),
    }
  })
}
